import { useState, useMemo, useRef, useEffect } from "react";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/components/ModalContainer";
import { SearchIcon, CheckIcon } from "@/components/Icons";
import { LANGUAGES } from "@/models/utils";
import { useTranslation } from "react-i18next";

const AlertLanguagePicker = () => {
  const { t } = useTranslation();
  const alertLanguagePicker = useUISlice((state) => state.alertLanguagePicker);
  const setAlertLanguagePicker = useUISlice(
    (state) => state.setAlertLanguagePicker
  );

  const [search, setSearch] = useState("");
  const selectedRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedLanguage = alertLanguagePicker?.selectedLanguage;

  const filteredLanguages = useMemo(() => {
    if (!search.trim()) return LANGUAGES;
    const searchLower = search.toLowerCase().trim();
    return LANGUAGES.filter((l) => l.label.toLowerCase().includes(searchLower));
  }, [search]);

  // Scroll to selected language on mount
  useEffect(() => {
    if (alertLanguagePicker && selectedRef.current && listRef.current) {
      setTimeout(() => {
        selectedRef.current?.scrollIntoView({
          block: "center",
          behavior: "instant",
        });
      }, 50);
    }
  }, [alertLanguagePicker]);

  const handleDismiss = () => {
    setSearch("");
    if (alertLanguagePicker?.onDismiss) {
      alertLanguagePicker.onDismiss();
    }
    setAlertLanguagePicker(undefined);
  };

  const handleSelect = (languageCode: string, languageName: string) => {
    if (alertLanguagePicker?.onSelect) {
      alertLanguagePicker.onSelect(languageCode, languageName);
    }
    handleDismiss();
  };

  if (!alertLanguagePicker) return null;

  return (
    <ModalContainer
      title={alertLanguagePicker.title || t("alertLanguagePicker.title")}
      onDismiss={handleDismiss}
      hideCancel
      modalWidth={400}
      contentClassName="!mb-0 !pb-0"
      removeBottomPadding={true}
    >
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("alertLanguagePicker.placeholder")}
            className="w-full h-12 pl-11 pr-4 bg-[var(--bg-secondary)] rounded-2xl text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none transition-all text-lg shadow-sm"
          />
        </div>

        {/* Languages list */}
        <div
          ref={listRef}
          className="max-h-[50vh] overflow-y-auto space-y-2 pb-4 px-1"
        >
          {filteredLanguages.map((lang) => {
            const isSelected = lang.value === selectedLanguage;
            return (
              <button
                key={lang.value}
                ref={isSelected ? selectedRef : undefined}
                onClick={() => handleSelect(lang.value, lang.label)}
                className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl transition-all text-left group ${
                  isSelected
                    ? "bg-[var(--bg-secondary)] shadow-md scale-[1.02]"
                    : "hover:bg-[var(--glass-bg-hover)] active:scale-[0.98]"
                }`}
              >
                <span
                  className={`text-lg flex-1 transition-colors ${
                    isSelected
                      ? "text-[var(--text-primary)] font-semibold"
                      : "text-[var(--text-secondary)]"
                  }`}
                >
                  {lang.label}
                </span>
                {isSelected && (
                  <div className="w-6 h-6 rounded-full bg-[var(--text-primary)] flex items-center justify-center">
                    <CheckIcon
                      className="w-4 h-4 text-[var(--bg-primary)]"
                      strokeWidth={3}
                    />
                  </div>
                )}
              </button>
            );
          })}

          {filteredLanguages.length === 0 && (
            <p className="text-center text-[var(--text-tertiary)] text-sm py-4">
              {t("alertLanguagePicker.noResults")}
            </p>
          )}
        </div>
      </div>
    </ModalContainer>
  );
};

export default AlertLanguagePicker;
